import React, { useState, useEffect } from 'react';
import { deviceCommandsApi, CommandStatus } from '../services/deviceCommandsApi';

interface DeviceControlPanelProps {
  deviceId: number;
  deviceName: string;
  onClose?: () => void;
}

export const DeviceControlPanel: React.FC<DeviceControlPanelProps> = ({ deviceId, deviceName, onClose }) => {
  const [activeCommand, setActiveCommand] = useState<CommandStatus | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const executeCommand = async (command: string) => {
    setError(null);
    setIsExecuting(true);

    try {
      // Issue command
      const response = await deviceCommandsApi.issueCommand(deviceId, command);
      
      // Start polling for result
      const result = await deviceCommandsApi.pollCommandCompletion(response.commandId);
      setActiveCommand(result);

      // Auto-clear after a few seconds if not screenshot
      if (command !== 'whats-on-screen') {
        setTimeout(() => setActiveCommand(null), 5000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Command failed');
    } finally {
      setIsExecuting(false);
    }
  };

  const renderCommandResult = () => {
    if (!activeCommand) return null;

    return (
      <div className="command-result">
        <div className="result-header">
          <h3>Command Result</h3>
          <button onClick={() => setActiveCommand(null)}>×</button>
        </div>

        {activeCommand.status === 'failed' && (
          <div className="result-error">
            Command failed: {activeCommand.result}
          </div>
        )}

        {activeCommand.status === 'completed' && (
          <>
            {activeCommand.resultImage && (
              <div className="result-image">
                <img
                  src={`data:image/png;base64,${activeCommand.resultImage}`}
                  alt="Screen capture"
                  style={{ width: '100%', height: 'auto' }}
                />
              </div>
            )}

            {activeCommand.result && (
              <div className="result-text">
                <pre>{activeCommand.result}</pre>
              </div>
            )}

            <div className="result-meta">
              Completed at: {new Date(activeCommand.completedAt!).toLocaleString()}
              {activeCommand.exitCode !== undefined && ` | Exit code: ${activeCommand.exitCode}`}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="device-control-panel">
      {/* Header */}
      <div className="panel-header">
        <div className="header-content">
          <h1>🖥️ {deviceName}</h1>
        </div>
        {onClose && (
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Quick Actions */}
      <div className="section">
        <h2>⚡ Quick Actions</h2>
        <div className="button-grid">
          <button
            className="btn btn-success"
            onClick={() => executeCommand('start-player')}
            disabled={isExecuting}
          >
            ▶️ Start Player
          </button>

          <button
            className="btn btn-warning"
            onClick={() => executeCommand('stop-player')}
            disabled={isExecuting}
          >
            ⏸️ Stop Player
          </button>

          <button
            className="btn btn-primary"
            onClick={() => executeCommand('restart-player')}
            disabled={isExecuting}
          >
            🔄 Restart Player
          </button>

          <button
            className="btn btn-primary"
            onClick={() => executeCommand('update-code')}
            disabled={isExecuting}
          >
            ⬆️ Update Code
          </button>

          <button
            className="btn btn-secondary"
            onClick={() => executeCommand('whats-on-screen')}
            disabled={isExecuting}
          >
            🖼️ What's on Screen?
          </button>

          <button
            className="btn btn-secondary"
            onClick={() => executeCommand('get-system-info')}
            disabled={isExecuting}
          >
            📊 Get System Info
          </button>

         <button
            className="btn btn-secondary"
            onClick={() => executeCommand('whats-on-camera')}
            disabled={isExecuting}
          >
            📸 What's on Camera?
          </button>

          <button
            className="btn btn-secondary"
            onClick={() => executeCommand('clear-log-files')}
            disabled={isExecuting}
          >
            📝 Clear Log Files
          </button>

          <button
            className="btn btn-secondary"
            onClick={() => executeCommand('test-camera')}
            disabled={isExecuting}
          >
            📷 Test Camera
          </button>

          <button
            className="btn btn-danger"
            onClick={() => {
              if (window.confirm('Are you sure you want to reboot this device?')) {
                executeCommand('reboot-system');
              }
            }}
            disabled={isExecuting}
          >
            🔄 Reboot System
          </button>
        </div>

        {isExecuting && (
          <div className="executing-message">
            ⏳ Executing command...
          </div>
        )}
      </div>

      {/* Command Result */}
      {renderCommandResult()}

      {/* Advanced Terminal (placeholder for future) */}
      <div className="section">
        <button
          className="advanced-toggle"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          <span>⚙️ Advanced Terminal</span>
          <span>{showAdvanced ? '▲' : '▼'}</span>
        </button>

        {showAdvanced && (
          <div className="advanced-content">
            <div className="warning-text">
              ⚠️ <strong>Warning:</strong> Terminal access requires administrator privileges.
              Coming soon!
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
